import React from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import {
  canAccessAdminSectionOrLegacy,
  canReadModule,
  getFirstAllowedAdministrationPath,
  getModuleCrud,
  isSystemAdmin,
} from "../utils/appRole";

/**
 * Map legacy permission IDs (helpers / docs) onto account-access module keys.
 */
const PERMISSION_MODULE_ALIASES = {
  users: "user_management",
  usermanagement: "user_management",
  user_management: "user_management",
  roles: "user_management",
  cameras: "camera_management",
  camera_management: "camera_management",
  omniwatch: "omniwatch",
  "omni-watch": "omniwatch",
  ai_detection: "ai_detection",
  alert_service: "alert_service",
  flashback: "flashback",
  nvr: "nvr_management",
  nvr_management: "nvr_management",
};

function resolveModuleFromPermission(permission) {
  const raw = String(permission ?? "").trim();
  if (!raw) return null;

  const crudMatch = raw.match(
    /^([\w-]+(?:\.[\w-]+)?)\.(read|create|update|delete)$/i,
  );
  if (crudMatch) {
    const base = crudMatch[1].replace(/\./g, "_").toLowerCase();
    const action = crudMatch[2].toLowerCase();
    const moduleKey = PERMISSION_MODULE_ALIASES[base] || base;
    return { moduleKey, action };
  }

  const key = raw.replace(/\./g, "_").toLowerCase();
  const moduleKey = PERMISSION_MODULE_ALIASES[key] || key;
  return { moduleKey, action: "read" };
}

function hasAccountPermission(accountAccess, permission) {
  if (isSystemAdmin(accountAccess)) return true;
  const resolved = resolveModuleFromPermission(permission);
  if (!resolved) return false;
  const crud = getModuleCrud(accountAccess, resolved.moduleKey);
  return Boolean(crud[resolved.action]);
}

function hasAnyAccountPermission(accountAccess, list) {
  return list.some((perm) => hasAccountPermission(accountAccess, perm));
}

function hasAllAccountPermissions(accountAccess, list) {
  return list.every((perm) => hasAccountPermission(accountAccess, perm));
}

/**
 * ProtectedRoute — primary route/component guard using GET /api/account/access (appRole).
 *
 * Route gating in App.js:
 * - `moduleKey` — module read (replaces former ModuleReadRoute / FlashbackRoute)
 * - legacy `permission` / `permissions` / `allPermissions` — PermissionWrapper-style checks
 * - Admin shell: AdminRoute; per-section children: AdminSectionRoute
 */
const ProtectedRoute = ({
  children,
  moduleKey,
  permission,
  permissions = [],
  allPermissions = [],
  redirectTo = "/omni-watch",
  fallback = null,
  requireAuth = true,
}) => {
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const accountAccess = useSelector((state) => state.auth.accountAccess);
  const accessLoaded = useSelector((state) => state.auth.accessLoaded);
  const loading = useSelector((state) => state.auth.loading);

  if (requireAuth && !isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requireAuth && (loading || !accessLoaded)) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        Loading…
      </div>
    );
  }

  if (isSystemAdmin(accountAccess)) {
    return children;
  }

  const denied = fallback || (
    <AccessDenied message="You don't have permission to access this page." />
  );

  if (moduleKey && !canReadModule(accountAccess, moduleKey)) {
    return denied;
  }

  if (
    !moduleKey &&
    !permission &&
    permissions.length === 0 &&
    allPermissions.length === 0
  ) {
    return children;
  }

  if (permission && !hasAccountPermission(accountAccess, permission)) {
    return denied;
  }

  if (permissions.length > 0 && !hasAnyAccountPermission(accountAccess, permissions)) {
    return denied;
  }

  if (
    allPermissions.length > 0 &&
    !hasAllAccountPermissions(accountAccess, allPermissions)
  ) {
    return denied;
  }

  return children;
};

/**
 * Gate a single Administration child route by administrationSections (or legacy modules).
 * On deny, redirect to the first allowed admin section (or /omni-watch).
 */
export function AdminSectionRoute({ sectionKey, children }) {
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const accountAccess = useSelector((state) => state.auth.accountAccess);
  const accessLoaded = useSelector((state) => state.auth.accessLoaded);
  const loading = useSelector((state) => state.auth.loading);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (loading || !accessLoaded) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        Loading…
      </div>
    );
  }

  if (!canAccessAdminSectionOrLegacy(accountAccess, sectionKey)) {
    const fallbackPath = getFirstAllowedAdministrationPath(accountAccess);
    if (fallbackPath) {
      return <Navigate to={`/administration/${fallbackPath}`} replace />;
    }
    return <Navigate to="/omni-watch" replace />;
  }

  return children;
}

/**
 * Permission-based component wrapper — same accountAccess rules as ProtectedRoute.
 */
export const PermissionWrapper = ({
  children,
  permission,
  permissions = [],
  allPermissions = [],
  fallback = null,
}) => {
  const accountAccess = useSelector((state) => state.auth.accountAccess);

  if (isSystemAdmin(accountAccess)) {
    return children;
  }

  if (!permission && permissions.length === 0 && allPermissions.length === 0) {
    return children;
  }

  if (permission) {
    return hasAccountPermission(accountAccess, permission)
      ? children
      : fallback || null;
  }

  if (permissions.length > 0) {
    return hasAnyAccountPermission(accountAccess, permissions)
      ? children
      : fallback || null;
  }

  if (allPermissions.length > 0) {
    return hasAllAccountPermissions(accountAccess, allPermissions)
      ? children
      : fallback || null;
  }

  return children;
};

/**
 * Access Denied Component
 * Shows when user doesn't have required permissions
 */
export const AccessDenied = ({
  message = "You don't have permission to access this page.",
}) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      height: "50vh",
      textAlign: "center",
      padding: "2rem",
    }}
  >
    <div style={{ fontSize: "4rem", marginBottom: "1rem" }}>🚫</div>
    <h2 style={{ color: "#e74c3c", marginBottom: "1rem" }}>Access Denied</h2>
    <p style={{ color: "#666", marginBottom: "2rem" }}>{message}</p>
    <button
      type="button"
      onClick={() => window.history.back()}
      style={{
        padding: "0.75rem 1.5rem",
        backgroundColor: "#3498db",
        color: "white",
        border: "none",
        borderRadius: "4px",
        cursor: "pointer",
      }}
    >
      Go Back
    </button>
  </div>
);

export default ProtectedRoute;
